import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useServer } from '../context/ServerContext';
import CreateCenters from './CreateCenters';
import { guardarEtiqueta, labelId, labelsRequest } from '../features/labels/labels';
import { useLabels } from '../features/labels/LabelsProvider';

export default function CentrosYCiclos({ translate }) {
  const { servidor } = useServer();
  const { refresh } = useLabels();
  const [params, setParams] = useSearchParams();
  const superuser = localStorage.getItem('cargo') === 'Superusuario';
  const requested = params.get('seccion') || 'centros';
  const section = superuser && ['areas', 'detectados'].includes(requested) ? requested : 'centros';
  const [catalog, setCatalog] = useState([]);
  const [detected, setDetected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [editCentro, setEditCentro] = useState(null);
  const [drafts, setDrafts] = useState({});
  const t = (key) => translate(`centros_${key}`);
  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await labelsRequest(servidor, section === 'areas' ? '/etiquetas/catalogo' : '/centros/detectados');
      if (!Array.isArray(data)) throw new Error('invalid_catalog');
      if (section === 'areas') setCatalog(data);
      else setDetected(data);
    } catch { setError(t('load_error')); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (superuser && section !== 'centros') load(); }, [section, servidor, superuser]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (item) => {
    const key = `${item.tipo}:${item.slug}`;
    setBusy(key); setError(''); setSuccess(false);
    try {
      await guardarEtiqueta(servidor, item.tipo, item.slug, drafts[key] ?? item.label ?? '');
      await refresh(); await load(); setSuccess(true);
    } catch (e) { setError(t(e.status === 409 ? 'conflict' : 'save_error')); }
    finally { setBusy(''); }
  };
  const confirm = async (item) => {
    setBusy(item.centro_id); setError(''); setSuccess(false);
    try {
      await labelsRequest(servidor, `/centros/detectados/${encodeURIComponent(item.centro_id)}/confirmar`, { method: 'POST' });
      await load(); setSuccess(true);
    } catch { await load(); setError(t('confirm_error')); }
    finally { setBusy(''); }
  };
  return <Box sx={{ width: '100%', minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Tabs value={section} onChange={(_, value) => { setError(''); setSuccess(false); setParams({ seccion: value }); }}
      variant="scrollable" scrollButtons="auto" aria-label={t('title')} sx={{ mb: 2, flexShrink: 0 }}>
      <Tab value="centros" label={t('tab_centers')} />
      {superuser && <Tab value="areas" label={t('tab_areas')} />}
      {superuser && <Tab value="detectados" label={t('tab_detected')} />}
    </Tabs>
    {section === 'centros' ? <CreateCenters translate={translate} editCentro={editCentro} onEditConsumed={() => setEditCentro(null)} /> : <Box sx={{ p: { xs: 1, md: 3 }, minHeight: 0, overflowY: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} sx={{ mb: 2, flexShrink: 0 }}>
        <Typography variant="h6">{t(section === 'areas' ? 'tab_areas' : 'tab_detected')}</Typography>
        <Button onClick={load} disabled={loading || !!busy}>{t('refresh')}</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, flexShrink: 0 }}>{t('saved')}</Alert>}
      {loading ? <CircularProgress aria-label={t('loading')} /> : section === 'areas' ? <Stack spacing={2}>
        {!catalog.some((x) => x.tipo !== 'centro') && !error && <Alert severity="info">{t('empty_areas')}</Alert>}
        {catalog.filter((x) => x.tipo !== 'centro').map((item) => {
          const key = `${item.tipo}:${item.slug}`;
          return <Stack key={key} direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ md: 'center' }}
            sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ flex: 1 }}><Typography>{t(item.tipo)}</Typography><Typography variant="body2" color="text.secondary">{item.slug}</Typography></Box>
            <TextField label={t('visible_name')} value={drafts[key] ?? item.label ?? ''} disabled={item.conflicto || !!busy}
              inputProps={{ maxLength: 60 }} onChange={(e) => setDrafts({ ...drafts, [key]: e.target.value })}
              helperText={item.conflicto ? t('conflict') : t('name_help')} sx={{ flex: 2 }} />
            <Button onClick={() => save(item)} disabled={item.conflicto || !!busy}>{t('save')}</Button>
          </Stack>;
        })}
      </Stack> : <Stack spacing={2}>
        {!detected.length && !error && <Alert severity="info">{t('empty_detected')}</Alert>}
        {detected.map((item) => {
          const partial = item.estado_alta === 'confirmado';
          const service = item.autodeteccion?.servicio;
          return <Box key={item.centro_id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="h6">{labelId(item.centro_id, 'car', item.centro_id)}</Typography>
            <Typography color="text.secondary" variant="body2">{item.centro_id}</Typography>
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ my: 1 }}>
              <Chip size="small" label={service?.toUpperCase()} />
              <Chip size="small" color={partial ? 'warning' : 'default'} label={t(partial ? 'partial' : 'pending')} />
            </Stack>
            <Typography variant="body2">{t('received')}: {item.autodeteccion?.fecha ? new Date(item.autodeteccion.fecha).toLocaleString() : '—'}</Typography>
            <Typography variant="body2">{t('window')}: {item[service]?.init || '—'} → {item[service]?.fin || '—'}</Typography>
            {partial && <Alert severity="warning" sx={{ my: 1 }}>{t('partial_warning')}</Alert>}
            {!item.autodeteccion?.alta_completa && <Alert severity="info" sx={{ my: 1 }}>{t('incomplete')}</Alert>}
            <Stack direction="row" gap={2} sx={{ mt: 2 }}>
              <Button variant="contained" disabled={!!busy} onClick={() => confirm(item)}>{t(partial ? 'retry' : 'confirm')}</Button>
              <Button disabled={!!busy} onClick={() => { setEditCentro(item); setParams({ seccion: 'centros' }); }}>{t('edit_cycle')}</Button>
            </Stack>
          </Box>;
        })}
      </Stack>}
    </Box>}
  </Box>;
}
